import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Menu, Settings, X } from "lucide-react";
import { useState } from "react";

const NavItem = ({ to, children, testId }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={testId}
    className={({ isActive }) =>
      `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
        isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
      }`
    }
  >
    {children}
  </NavLink>
);

const ROLE_LABEL = {
  admin: "Admin",
  operator_mayor: "Op. Mayor",
  operator_detalle: "Op. Detalle",
  readonly: "Solo Lectura",
};

export default function Layout() {
  const { user, logout, isAdmin, isOpMayor, isOpDetalle, isReadOnly, isOperator, canSeeFinancial } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
              <div className="w-9 h-9 rounded-lg bg-slate-900 text-white grid place-items-center font-bold">C</div>
              <span className="font-bold text-slate-900 tracking-tight">CryptoBuy</span>
            </Link>

            <nav className="hidden xl:flex items-center gap-1" data-testid="main-nav">
              <NavItem to="/" testId="nav-dashboard">Dashboard</NavItem>
              <NavItem to="/compra-venta" testId="nav-compraventa">Compra/Venta</NavItem>
              <NavItem to="/transacciones" testId="nav-transacciones">Transacciones</NavItem>
              {(isAdmin || isReadOnly) && <NavItem to="/inventario" testId="nav-inventario">Inventario</NavItem>}
              {(isAdmin || isReadOnly) && <NavItem to="/reportes" testId="nav-reportes">Reportes</NavItem>}
              {(isAdmin || isOpDetalle) && <NavItem to="/clientes" testId="nav-clientes">Clientes</NavItem>}
              {(isAdmin || isOpDetalle) && <NavItem to="/referidos" testId="nav-referidos">Referidos</NavItem>}
              {isOperator && <NavItem to="/correcciones" testId="nav-correcciones">Correcciones</NavItem>}
              {(isAdmin || isReadOnly) && <NavItem to="/auditoria" testId="nav-auditoria">Auditoría</NavItem>}
              {isAdmin && <NavItem to="/usuarios" testId="nav-usuarios">Usuarios</NavItem>}
            </nav>

            <div className="flex items-center gap-3">
              <div className="hidden md:flex flex-col items-end leading-tight">
                <span className="text-sm font-semibold text-slate-800" data-testid="user-name">{user?.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500" data-testid="user-role">{ROLE_LABEL[user?.role] || user?.role}</span>
              </div>
              <Link to="/configuracion" data-testid="nav-configuracion" className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" title="Configuracion"><Settings size={18} /></Link>
              <button onClick={onLogout} data-testid="logout-button" className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" title="Cerrar sesión">
                <LogOut size={18} />
              </button>
              <button onClick={() => setOpen((v) => !v)} className="xl:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100" data-testid="mobile-menu-toggle">
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
          {open && (
            <div className="xl:hidden pb-3 flex flex-col gap-1" data-testid="mobile-nav">
              <NavItem to="/" testId="m-nav-dashboard">Dashboard</NavItem>
              <NavItem to="/compra-venta" testId="m-nav-compraventa">Compra/Venta</NavItem>
              <NavItem to="/transacciones" testId="m-nav-transacciones">Transacciones</NavItem>
              {(isAdmin || isReadOnly) && <NavItem to="/inventario" testId="m-nav-inventario">Inventario</NavItem>}
              {(isAdmin || isReadOnly) && <NavItem to="/reportes" testId="m-nav-reportes">Reportes</NavItem>}
              {(isAdmin || isOpDetalle) && <NavItem to="/clientes" testId="m-nav-clientes">Clientes</NavItem>}
              {(isAdmin || isOpDetalle) && <NavItem to="/referidos" testId="m-nav-referidos">Referidos</NavItem>}
              {isOperator && <NavItem to="/correcciones" testId="m-nav-correcciones">Correcciones</NavItem>}
              {(isAdmin || isReadOnly) && <NavItem to="/gastos-mayor" testId="m-nav-gastos">Gastos Mayor</NavItem>}
              {(isAdmin || isReadOnly) && <NavItem to="/cuadre" testId="m-nav-cuadre">Cuadre</NavItem>}
              {isAdmin && <NavItem to="/auditoria" testId="m-nav-auditoria">Auditoría</NavItem>}
              {isAdmin && <NavItem to="/usuarios" testId="m-nav-usuarios">Usuarios</NavItem>}
              <NavItem to="/configuracion" testId="m-nav-configuracion">Configuracion</NavItem>
            </div>
          )}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <Outlet />
      </main>
    </div>
  );
}
